import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

// Lazy imports for sub-dashboards
import Dashboard from './Dashboard';
import PODashboard from './PODashboard';
import PurchaseEntryDashboard from './PurchaseEntryDashboard';
import SODashboard from './SODashboard';
import GeoDashboard from './GeoDashboard';

const COLORS = ['#0ea5e9','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
const fmtCurrency = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); if (p.length === 3 && p[0].length === 4) return `${p[2]}-${p[1]}-${p[0].slice(-2)}`; return d; };

const SalesEntryDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try { const res = await api.get('/sales-entries/dashboard/stats'); setData(res.data); }
      catch { toast.error('Failed to load SE dashboard'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div></div>;
  if (!data) return <div className="p-6 text-center text-gray-500">No data available</div>;

  const { kpis, customer_breakdown, monthly_trends, recent_entries } = data;
  const tallyData = Object.entries(kpis.tally_counts || {}).map(([name, value]) => ({ name: name.replace('_', ' '), value }));
  const sourceData = Object.entries(kpis.source_counts || {}).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="se-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="se-kpis">
        <Card className="border-l-4 border-l-rose-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Total Entries</p>
          <p className="text-2xl font-bold text-gray-900">{kpis.total_entries}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-blue-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Total Qty Sold</p>
          <p className="text-2xl font-bold text-blue-700">{(kpis.total_qty || 0).toLocaleString()}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Total Amount</p>
          <p className="text-xl font-bold text-green-700">{fmtCurrency(kpis.total_amount)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-purple-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Total Items</p>
          <p className="text-2xl font-bold text-purple-700">{kpis.total_items}</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Sales Trend</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthly_trends}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => n === 'amount' ? fmtCurrency(v) : v} />
                <Bar dataKey="qty" fill="#0ea5e9" name="Qty" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Customers */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Top Customers by Qty</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customer_breakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                <Tooltip /><Bar dataKey="qty" fill="#10b981" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tally Status */}
        {tallyData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Tally Status</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={tallyData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {tallyData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Source Breakdown */}
        {sourceData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Order Source</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={sourceData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Entries */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Recent Sales Entries</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/80"><th className="text-left px-3 py-2">Entry #</th><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Source</th></tr></thead>
              <tbody>
                {(recent_entries || []).map((e, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-rose-700">{e.entry_number}</td>
                    <td className="px-3 py-2">{e.customer}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2 text-right">{e.qty}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCurrency(e.amount)}</td>
                    <td className="px-3 py-2"><Badge className="bg-purple-100 text-purple-700 text-[10px]">{(e.source || 'direct').replace(/_/g, ' ')}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'po', label: 'Purchase Orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  { id: 'pe', label: 'Purchase Entries', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
  { id: 'so', label: 'Sales Orders', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
  { id: 'se', label: 'Sales Entries', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'geo', label: 'India Map', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

const DashboardHub = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const setActiveTab = (tab) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="dashboard-hub">
      {/* Tab Navigation */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b" data-testid="dashboard-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap border-b-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-700 bg-blue-50/80'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`dashboard-tab-${tab.id}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div data-testid="dashboard-content">
        {activeTab === 'overview' && <Dashboard user={user} />}
        {activeTab === 'po' && <PODashboard user={user} />}
        {activeTab === 'pe' && <PurchaseEntryDashboard user={user} />}
        {activeTab === 'so' && <SODashboard user={user} />}
        {activeTab === 'se' && <SalesEntryDashboard user={user} />}
        {activeTab === 'geo' && <GeoDashboard user={user} />}
      </div>
    </div>
  );
};

export default DashboardHub;
